import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Popover, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import merge from "lodash/merge";
import { FC, useCallback, useState } from "react";

import { useI18N } from "@sundae/react-hooks";
import { testIds } from "../../constants/testIds.constants";
import { PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import PoolComponent from "../PoolComponent";
import { IPoolComponentProps } from "../PoolComponent/PoolComponent";
import PoolPickerItem from "./PoolPickerItem";
import PoolPickerSkeleton from "./PoolPickerSkeleton";

interface IPoolPickerProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  onPoolSelect: (pool: PoolFragmentFragment) => void;
  poolComponentProps?: Partial<IPoolComponentProps>;
  pools: PoolFragmentFragment[];
  selectedPool: PoolFragmentFragment;
}

const PoolPicker: FC<IPoolPickerProps> = ({
  isDisabled,
  isLoading,
  onPoolSelect,
  poolComponentProps,
  pools,
  selectedPool,
}) => {
  const { t } = useI18N("exchange");
  const [isOpen, setIsOpen] = useState(false);
  const defaultPoolComponentProps: IPoolComponentProps = merge(
    {
      inline: true,
      pool: selectedPool,
      small: true,
    },
    poolComponentProps
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      if (isDisabled) return;
      setIsOpen(open);
    },
    [isDisabled]
  );

  const handlePoolSelect = useCallback(
    (pool: PoolFragmentFragment) => {
      setIsOpen(false);
      onPoolSelect(pool);
    },
    [onPoolSelect]
  );

  if (isLoading) {
    return <PoolPickerSkeleton />;
  }

  return (
    <Popover
      // TODO: Refactor ui toolkit with updated UI
      primitiveProps={{
        content: {
          className: cx(
            "!p-0",
            "overflow-hidden !min-w-0 w-fit",
            "relative !z-50",
            toolkitCx.layout.background.default,
            toolkitCx.layout.border.default,
            toolkitCx.layout.border.weight.default,
            toolkitCx.recipes.blurryCorner.primary
          ),
        },
      }}
      trigger={
        // TODO: Refactor this to reusable component at some point (used across the app, i.e. Liquidity Actions)
        <div
          data-testid={testIds.exchangeFlow.orderControls.liquidityPoolDropdown.select}
          aria-disabled={isDisabled}
          className={cx(
            "flex select-none items-center rounded-lg",
            toolkitCx.layout.spacing.both.sm,
            toolkitCx.layout.border.default,
            toolkitCx.layout.border.weight.default,
            toolkitCx.layout.transition.default,
            toolkitCx.recipes.scaleWhenActive,
            {
              "gap-2": defaultPoolComponentProps.inline,
              "gap-4": !defaultPoolComponentProps.inline,
              "cursor-not-allowed opacity-75": isDisabled,
              [`${toolkitCx.layout.background.hoverable} cursor-pointer`]: !isDisabled,
            }
          )}
        >
          <PoolComponent {...defaultPoolComponentProps} pool={selectedPool} />
          <ChevronDownIcon
            className={cx(
              {
                "h-[11px] w-[11px]": defaultPoolComponentProps.inline,
                "h-3 w-3": !defaultPoolComponentProps.inline,
                "-scale-y-100": isOpen,
              },
              toolkitCx.layout.text.default,
              toolkitCx.layout.transition.transform
            )}
          />
        </div>
      }
      open={isOpen}
      onOpenChange={handleOnOpenChange}
      withCloseIcon={false}
    >
      <div
        className={cx(
          "flex items-center justify-between",
          "border-b border-b-gray-300 dark:border-b-gray-600",
          toolkitCx.layout.spacing.both.md
        )}
      >
        <Text tag="h3" size="sm">
          {t("liquidityPoolSelect.title", {
            context: "popover",
          })}
        </Text>
        <XMarkIcon
          className={cx("h-3 w-3 cursor-pointer", toolkitCx.layout.text.default)}
          onClick={handleClose}
        />
      </div>
      <ul role="listbox" className="relative z-10 grid motion-safe:animate-slide-down">
        {pools.map((pool) => (
          <PoolPickerItem
            key={pool.ident}
            isSelected={pool.ident === selectedPool.ident}
            onPoolSelect={handlePoolSelect}
            poolComponentProps={defaultPoolComponentProps}
            pool={pool}
          />
        ))}
      </ul>
    </Popover>
  );
};

export default PoolPicker;
